// src/prototype/pages/EAPrintableReportPage.tsx
// ============================================================
// Standalone print-ready Portfolio Strategy Report page.
// Route: /report/print (opens in new tab, no AppShell)
//
// QI-2 Improvements (March 2026):
//   1. Watermark opacity corrected to 0.06
//   2. Cover page: added subtitle + executive footer line
//   3. New section: Strategic Investment Recommendation
//   4. New section: Key Strategic Insights
//   5. Interpretation paragraphs on every existing section
//   6. Methodology appendix as final section
//
// QI-3 Improvements (March 2026):
//   7. Print page-break behavior hardened across all sections
//   8. Atomic block protection via pir-atomic utility class
//   9. OIR sub-component cards protected via CSS selectors
//  10. Interactive controls (buttons, selects) hidden in print
//  11. Section intro band pattern standardized (PrintSectionHeader divider)
//  12. h2/h3/h4 break-after: avoid to keep headers with content
//  13. Orphan/widow control on paragraphs
//
// QI-Refinement Pass (March 2026) — Trust, Density & Usability:
//  14. OIRSectionStrategyDecisionCanvas removed (broken "Launch Now: None" state)
//  15. "How to Interpret This Report" usage guidance block added before Section 1
//  16. All NarrativeBlock prose compressed ~25%; paragraphs → bullets where applicable
//  17. NarrativeBlock spacing tightened (margin, padding, font-size)
//  18. Currency format standardized: "$X.XM – $X.XM" throughout
//  19. Unused noop declaration removed
//
// No OIR section logic was changed. No in-app UI was changed.
// ============================================================

import React, { useMemo } from 'react';
import { isQuickWin as badgeIsQuickWin, effortTierForBadge } from '../config/opportunityBadge';
import { Printer, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ── Data sources ───────────────────────────────────────────────────────────
import { DEMO_CLIENT } from '../config/demoClient';
import { useCoverage } from '../hooks/useCoverage';
import { personalizationPercent } from '../../lib/coverage/personalizationPercent';
import { DEMO_OPPORTUNITIES } from '../data/demoOpportunities';

// ── OIR section components (reused exactly as-is) ──────────────────────────
import { OIRSectionExecutiveSummary } from '../components/oir/report/OIRSectionExecutiveSummary';
import { OIRSectionStrategicContext } from '../components/oir/report/OIRSectionStrategicContext';
import { OIRSectionOpportunityLandscape } from '../components/oir/report/OIRSectionOpportunityLandscape';
import { OIRSectionFinancialImpact } from '../components/oir/report/OIRSectionFinancialImpact';
import { OIRSectionExecutionRoadmap } from '../components/oir/report/OIRSectionExecutionRoadmap';

import { computeIncludedCount } from '../utils/opportunityRanking';
import { getDefaultCategoryWeightsMap } from '../utils/weighting';
// ── Helpers ────────────────────────────────────────────────────────────────
const toMillions = (v: number): number => (v > 10_000 ? v / 1_000_000 : v);

const computeRangeLabel = (opps: any[]): string => {
  if (!opps.length) return '$0';
  const totalLow = opps.reduce((s, o) => s + toMillions(o.roiEstimate?.low ?? 0), 0);
  const totalHigh = opps.reduce((s, o) => s + toMillions(o.roiEstimate?.high ?? 0), 0);
  return `$${totalLow.toFixed(1)}M – $${totalHigh.toFixed(1)}M`;
};

const formatDate = (): string => {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// ── Reusable narrative block styling ───────────────────────────────────────
const NarrativeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="pir-atomic"
    style={{
      margin: '10px 0 16px 0',
      padding: '12px 16px',
      backgroundColor: '#F8F9FB',
      borderLeft: '3px solid #FF6E4C',
      borderRadius: '0 8px 8px 0',
      fontSize: '12px',
      lineHeight: 1.65,
      color: '#374151',
      fontFamily: 'Montserrat, sans-serif',
    }}
  >
    {children}
  </div>
);

// ── Section header for new print-only sections ─────────────────────────────
// QI-3: Added bottom divider line to complete the section intro band pattern.
// This creates consistent visual separation between section header and body
// across all sections authored in this file.
const PrintSectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
    <h2
      style={{
        fontSize: '20px',
        fontWeight: 700,
        color: '#18365E',
        margin: 0,
        fontFamily: 'Montserrat, sans-serif',
      }}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        style={{
          fontSize: '13px',
          color: '#717182',
          marginTop: '4px',
          marginBottom: 0,
          fontFamily: 'Montserrat, sans-serif',
        }}
      >
        {subtitle}
      </p>
    )}
    <div style={{ width: '48px', height: '3px', backgroundColor: '#FF6E4C', borderRadius: '2px', marginTop: '12px' }} />
  </div>
);

// ── Component ──────────────────────────────────────────────────────────────
export default function EAPrintableReportPage() {
  const navigate = useNavigate();

  // Derived, so the printed report and the on-screen widget cannot disagree.
  const personalizationPct = personalizationPercent(useCoverage().aggregates);

  // ── Derive report data from demo opportunities ─────────────────────────
  const {
    rankedOpportunities,
    top10,
    selectedOpportunities,
    finalistCount,
    includedCount,
    portfolioRangeLabel,
    shortlistedRangeLabel,
    clientName,
    quickWinCount,
    firstValueHorizon,
  } = useMemo(() => {
    const all = [...DEMO_OPPORTUNITIES].sort(
      (a: any, b: any) => (a.rank ?? 999) - (b.rank ?? 999)
    );
    const t10 = all.slice(0, 10);
    // For the EA Preview, treat top 5 as the "selected" portfolio
    const selected = t10.slice(0, 5);

    const quickWins = selected.filter((o: any) => badgeIsQuickWin({ id: o.id, implementationEffort: o.implementationEffort })).length;
    const hasHigh   = selected.some((o: any) => effortTierForBadge({ id: o.id, implementationEffort: o.implementationEffort }) === 'High');

    return {
      rankedOpportunities: all,
      top10: t10,
      selectedOpportunities: selected,
      finalistCount: selected.length,
      includedCount: computeIncludedCount(getDefaultCategoryWeightsMap()),
      portfolioRangeLabel: computeRangeLabel(t10),
      shortlistedRangeLabel: computeRangeLabel(selected),
      clientName: DEMO_CLIENT.name,
      quickWinCount: quickWins,
      firstValueHorizon: hasHigh ? '12 months' : '6–9 months',
    };
  }, []);

  // ── Set document title for PDF filename default ────────────────────────
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = `Portfolio Strategy Report – ${clientName} – ${formatDate()}`;
    return () => { document.title = prevTitle; };
  }, [clientName]);

  return (
    <>
      {/* ── Print Stylesheet ─────────────────────────────────────────── */}
      <style>{`
        @media print {
          .pir-no-print { display: none !important; }

          @page {
            size: letter;
            margin: 0.75in 0.65in;
          }

          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: white !important;
          }

          /* ── Interactive controls: always hidden in print ──────────── */
          /* Hides scenario toggles, Reset, Finalize Portfolio,           */
          /* and all Assign execution posture dropdowns in the canvas.    */
          button { display: none !important; }
          select { display: none !important; }

          /* ── Section-level page control ────────────────────────────── */
          .pir-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .pir-section-break {
            page-break-before: always;
            break-before: always;
          }

          /* ── Atomic block protection ────────────────────────────────── */
          /* Applied to individual cards, rows, panels, and              */
          /* NarrativeBlocks authored directly in this file.             */
          .pir-atomic {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── OIR sub-component card protection ──────────────────────── */
          /* Targets Tailwind-classed card-like elements inside OIR       */
          /* section components without modifying those files.            */
          .pir-section [class*="rounded-lg"],
          .pir-section [class*="rounded-xl"],
          .pir-section [class*="rounded-md"],
          .pir-section [class*="rounded-"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── Inline-styled card protection ──────────────────────────── */
          /* Catches all divs with inline border-radius (our authored     */
          /* cards in Strategic Investment Recommendation, Methodology,   */
          /* Key Strategic Insights, etc.).                               */
          .pir-section [style*="border-radius"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── Grid and flex child protection ─────────────────────────── */
          /* Prevents grid/flex children from splitting unpredictably.    */
          .pir-section [class*="grid"] > *,
          .pir-section [class*="flex-col"] > *,
          .pir-section [style*="display: grid"] > *,
          .pir-section [style*="display: flex"] > * {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── Heading / content separation ───────────────────────────── */
          /* Ensures section and sub-section headings always stay         */
          /* visually attached to the content that follows them.          */
          h1, h2, h3, h4, h5 {
            break-after: avoid;
            page-break-after: avoid;
          }

          /* ── Orphan / widow control ─────────────────────────────────── */
          p {
            orphans: 3;
            widows: 3;
          }

          /* ── QI-2: Watermark at correct opacity ─────────────────────── */
          .pir-watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-35deg);
            font-size: 72px;
            font-weight: 800;
            color: rgba(24, 54, 94, 0.06);
            letter-spacing: 8px;
            white-space: nowrap;
            pointer-events: none;
            z-index: 0;
          }

          .pir-content {
            position: relative;
            z-index: 1;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }

        /* QI-2: On-screen watermark at 0.06 opacity */
        @media screen {
          .pir-watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-35deg);
            font-size: 72px;
            font-weight: 800;
            color: rgba(24, 54, 94, 0.06);
            letter-spacing: 8px;
            white-space: nowrap;
            pointer-events: none;
            z-index: 0;
          }
        }
      `}</style>

      {/* ── Watermark ────────────────────────────────────────────────── */}
      <div className="pir-watermark" aria-hidden="true">
        EARLY ADOPTER PREVIEW
      </div>

      {/* ── Print Toolbar (hidden in print) ──────────────────────────── */}
      <div className="pir-no-print sticky top-0 z-50 bg-white border-b border-[#e5e7eb] px-6 py-3 flex items-center justify-between"
           style={{ fontFamily: 'Montserrat, sans-serif' }}>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/opportunities')}
            className="flex items-center gap-2 text-sm text-[#717182] hover:text-[#18365E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Evaluate
          </button>
          <div className="h-5 w-px bg-[#e5e7eb]" />
          <span className="text-sm font-semibold text-[#18365E]">
            Portfolio Strategy Report — Print Preview
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 bg-[#18365E] text-white text-sm font-semibold rounded-lg hover:bg-[#18365E]/90 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print / Save as PDF
          </button>
          <span className="text-xs text-[#717182]">
            Tip: In Print dialog, select "Save as PDF" for a digital copy
          </span>
        </div>
      </div>

      {/* ── Report Body ──────────────────────────────────────────────── */}
      <div className="pir-content" style={{ fontFamily: 'Montserrat, sans-serif' }}>

        {/* ════════════════════════════════════════════════════════════ */}
        {/* PAGE 1: BRANDED HEADER + INSIGHT CONTENT                    */}
        {/* Replaces the standalone cover — decision intelligence starts */}
        {/* immediately on page 1.                                       */}
        {/* ════════════════════════════════════════════════════════════ */}

        {/* ── Compact branded header strip ─────────────────────────── */}
        <div
          style={{
            backgroundColor: '#18365E',
            padding: '20px 40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <img
              src="/public/adaptive_product_logo_white.png"
              alt="Adaptive Product"
              style={{ height: '24px', width: 'auto' }}
            />
            <div style={{ width: '1px', height: '28px', backgroundColor: 'rgba(255,255,255,0.18)' }} />
            <div>
              <p style={{ color: '#FF6E4C', fontSize: '10px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', margin: 0 }}>
                Portfolio Strategy Report
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', margin: '2px 0 0 0', fontWeight: 500 }}>
                Strategy Intelligence Assessment · Data Value Accelerator
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ color: 'rgba(255,255,255,0.92)', fontSize: '13px', fontWeight: 700, margin: 0 }}>{clientName}</p>
            <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: '11px', margin: '3px 0 0 0' }}>{formatDate()}</p>
          </div>
        </div>

        {/* Coral accent line */}
        <div style={{ height: '3px', backgroundColor: '#FF6E4C' }} />

        {/* ── Page 1 insight content ────────────────────────────────── */}
        <div style={{ backgroundColor: '#ffffff', padding: '28px 40px 30px 40px', fontFamily: 'Montserrat, sans-serif' }}>

          {/* Section label */}
          <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#FF6E4C', margin: '0 0 10px 0' }}>
            Executive Intelligence Summary
          </p>

          {/* Lead paragraph */}
          <p style={{ fontSize: '14px', lineHeight: 1.7, color: '#18365E', fontWeight: 600, margin: '0 0 22px 0', maxWidth: '680px' }}>
            Of the 174 use cases in Adaptive's evaluation library, {rankedOpportunities.length} were
            advanced to full evaluation for {clientName}'s portfolio; {includedCount} met the
            inclusion floor across 7 strategic domains. This surfaced {finalistCount} priority
            opportunities with a combined projected ROI
            of <strong style={{ color: '#FF6E4C' }}>{shortlistedRangeLabel}</strong>.
            The top 10 evaluated initiatives represent {portfolioRangeLabel} in addressable value.
          </p>

          {/* 6 KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '22px' }}>
            {([
              { label: 'Portfolio Value',         value: portfolioRangeLabel,                sub: 'Top 10 · conservative–aggressive span'             },
              { label: 'Shortlisted Value',        value: shortlistedRangeLabel,              sub: `Top ${finalistCount} · conservative–aggressive span` },
              { label: 'Use Case Library',         value: '174',                              sub: 'Full evaluation library'        },
              { label: 'Quick Wins Identified',    value: String(quickWinCount),              sub: 'Low-effort, high-value'         },
              { label: 'Time to First Value',      value: firstValueHorizon,                  sub: 'Estimated delivery'             },
              { label: 'Personalization Level',    value: `${personalizationPct}%`,           sub: 'Input strength across scoring measures' },
            ] as const).map(({ label, value, sub }) => (
              <div
                key={label}
                style={{
                  backgroundColor: '#F0F4F8',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  padding: '14px 16px',
                }}
              >
                <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#717182', margin: '0 0 7px 0' }}>{label}</p>
                <p style={{ fontSize: '19px', fontWeight: 800, color: '#18365E', margin: '0 0 2px 0', lineHeight: 1.1 }}>{value}</p>
                <p style={{ fontSize: '11px', color: '#717182', margin: 0 }}>{sub}</p>
              </div>
            ))}
          </div>

          {/* Top 3 recommended initiatives */}
          <div style={{ marginBottom: '18px' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, color: '#18365E', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Top Recommended Initiatives
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {selectedOpportunities.slice(0, 3).map((opp: any, i: number) => (
                <div
                  key={opp.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 14px',
                    backgroundColor: i === 0 ? '#F0F4F8' : '#F8F9FB',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${i === 0 ? '#FF6E4C' : '#e5e7eb'}`,
                  }}
                >
                  <span
                    style={{
                      width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                      backgroundColor: i === 0 ? '#FF6E4C' : '#18365E',
                      color: '#fff', fontSize: '11px', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {i + 1}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#18365E', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {opp.title}
                    </p>
                    <p style={{ fontSize: '11px', color: '#717182', margin: '2px 0 0 0' }}>{opp.category}</p>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#18365E', flexShrink: 0 }}>
                    {(() => {
                      const low  = toMillions(opp.roiEstimate?.low  ?? 0);
                      const high = toMillions(opp.roiEstimate?.high ?? 0);
                      return (low === 0 && high === 0) ? 'ROI modeled' : `$${low.toFixed(1)}M – $${high.toFixed(1)}M`;
                    })()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Governance readiness statement */}
          <div
            style={{
              backgroundColor: '#18365E',
              borderRadius: '10px',
              padding: '15px 20px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
            }}
          >
            <div style={{ width: '3px', alignSelf: 'stretch', backgroundColor: '#FF6E4C', borderRadius: '2px', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '10px', fontWeight: 700, color: '#FF6E4C', margin: '0 0 5px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Governance &amp; Decision Readiness
              </p>
              <p style={{ fontSize: '12px', lineHeight: 1.7, color: 'rgba(255,255,255,0.82)', margin: 0 }}>
                This report provides the structured evidence base required for executive review and governance submission —
                including standardized scoring, transparent assumptions, and initiative-level ROI documentation.
                All projections are traceable to source inputs within the DVA platform. The Strategic Execution
                Plan and Use Case Profiles are available as companion artifacts supporting capital allocation decisions.
              </p>
            </div>
          </div>

        </div>

        {/* Thin section divider before full report sections */}
        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0' }} />

        {/* ════════════════════════════════════════════════════════════ */}
        {/* REPORT SECTIONS                                             */}
        {/* ════════════════════════════════════════════════════════════ */}
        <div className="bg-white" style={{ padding: '32px 40px' }}>

          {/* ── How to Interpret This Report ─────────────────────── */}
          <div className="pir-atomic" style={{ marginBottom: '28px', padding: '18px 20px', backgroundColor: '#F0F4F8', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#18365E', margin: '0 0 10px 0' }}>
              How to Interpret This Report
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {[
                { label: 'Page 1', text: 'Executive decision summary — portfolio value, top initiatives, and governance readiness.' },
                { label: 'Section 1', text: 'Full portfolio metrics, initiative rankings, and recommended investment rationale.' },
                { label: 'Sections 2–4', text: 'Strategic context, opportunity landscape, and detailed financial impact analysis.' },
                { label: 'Section 5', text: 'Execution roadmap — initiative sequencing by effort tier and time-to-value.' },
                { label: 'Sections 6–7', text: 'Key strategic insights and methodology for governance review and CFO scrutiny.' },
              ].map(({ label, text }) => (
                <div key={label} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#FF6E4C', flexShrink: 0, width: '72px' }}>{label}</span>
                  <span style={{ fontSize: '11px', color: '#374151', lineHeight: 1.5 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── 1. Executive Summary ─────────────────────────────────── */}
          <div className="pir-section pir-section-break">
            <OIRSectionExecutiveSummary
              clientName={clientName}
              portfolioRangeLabel={portfolioRangeLabel}
              shortlistedRangeLabel={shortlistedRangeLabel}
              selectedCount={finalistCount}
              includedCount={includedCount}
              top10={top10}
              selectedOpportunities={selectedOpportunities}
            />
            <NarrativeBlock>
              <strong>Portfolio range</strong> ({portfolioRangeLabel}) = combined ROI across the top 10 evaluated initiatives.{' '}
              <strong>Shortlisted range</strong> ({shortlistedRangeLabel}) = the {finalistCount} highest-priority initiatives recommended for active investment.
              Initiative-level ROI modeling, risk profiles, and feasibility detail are in the individual Use Case Profiles.
              Delivery sequencing and governance structure are in the Strategic Execution Plan.
            </NarrativeBlock>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/* QI-2 NEW: Strategic Investment Recommendation               */}
          {/* ════════════════════════════════════════════════════════════ */}
          <div className="pir-section pir-section-break">
            <PrintSectionHeader
              title="Strategic Investment Recommendation"
              subtitle="Recommended portfolio based on DVA evaluation"
            />

            <NarrativeBlock>
              <strong>Recommended portfolio:</strong> top {finalistCount} initiatives from the {includedCount} that cleared the inclusion floor, selected on four criteria:
              <ul style={{ margin: '6px 0 0 0', padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <li>Strongest composite score across financial return, strategic alignment, implementation feasibility, and data readiness</li>
                <li>Balanced effort mix — quick wins for near-term momentum, strategic investments for long-term capability</li>
                <li>Finance-ready documentation for each initiative available in the CFO ROI Summary and Assumptions Appendix</li>
              </ul>
            </NarrativeBlock>

            {/* Recommendation cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div
                className="pir-atomic"
                style={{
                  padding: '20px',
                  backgroundColor: '#F0F4F8',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#717182', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Recommended Portfolio Value
                </p>
                <p style={{ fontSize: '24px', fontWeight: 800, color: '#18365E', marginBottom: '4px' }}>
                  {shortlistedRangeLabel}
                </p>
                <p style={{ fontSize: '12px', color: '#717182' }}>
                  Projected ROI across {finalistCount} selected initiatives
                </p>
              </div>
              <div
                className="pir-atomic"
                style={{
                  padding: '20px',
                  backgroundColor: '#F0F4F8',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#717182', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                  Full Evaluated Pipeline
                </p>
                <p style={{ fontSize: '24px', fontWeight: 800, color: '#18365E', marginBottom: '4px' }}>
                  {portfolioRangeLabel}
                </p>
                <p style={{ fontSize: '12px', color: '#717182' }}>
                  Combined value across top 10 evaluated opportunities
                </p>
              </div>
            </div>

            <div
              className="pir-atomic"
              style={{
                padding: '20px',
                backgroundColor: '#18365E',
                borderRadius: '10px',
                color: '#ffffff',
              }}
            >
              <p style={{ fontSize: '13px', fontWeight: 700, marginBottom: '10px', color: '#FF6E4C' }}>
                Investment Rationale
              </p>
              <p style={{ fontSize: '13px', lineHeight: 1.7, color: 'rgba(255,255,255,0.85)' }}>
                The recommended portfolio balances quick-win opportunities with longer-horizon
                strategic investments. Initiatives were selected based on their composite score across
                38 prioritization measures — spanning financial impact, strategic alignment,
                implementation complexity, and data readiness. This portfolio is designed to deliver
                measurable ROI within 6–18 months while building organizational capability for
                sustained analytics-driven value creation.
              </p>
            </div>

            {/* Top initiatives quick reference */}
            <div style={{ marginTop: '20px' }}>
              <p style={{ fontSize: '12px', fontWeight: 700, color: '#18365E', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Recommended Initiatives
              </p>
              {selectedOpportunities.slice(0, 5).map((opp: any, i: number) => (
                <div
                  key={opp.id}
                  className="pir-atomic"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 14px',
                    backgroundColor: i % 2 === 0 ? '#F8F9FB' : '#ffffff',
                    borderRadius: '6px',
                    marginBottom: '2px',
                  }}
                >
                  <span style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#FF6E4C',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {i + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#18365E', margin: 0 }}>
                      {opp.title}
                    </p>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#717182' }}>
                    {opp.category}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#18365E' }}>
                    {(() => {
                      const low = toMillions(opp.roiEstimate?.low ?? 0);
                      const high = toMillions(opp.roiEstimate?.high ?? 0);
                      return (low === 0 && high === 0)
                        ? 'ROI varies across initiatives'
                        : `$${low.toFixed(1)}M – $${high.toFixed(1)}M`;
                    })()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── 2. Strategic Context + QI-2 interpretation ───────────── */}
          <div className="pir-section pir-section-break">
            <OIRSectionStrategicContext clientName={clientName} />
            <NarrativeBlock>
              Ranking and weighting reflect {clientName}'s organizational profile, survey inputs, and uploaded documentation —
              calibrated to your institution's specific priorities and constraints rather than generic industry benchmarks alone.
            </NarrativeBlock>
          </div>

          {/* ── 3. Opportunity Landscape + QI-2 interpretation ───────── */}
          <div className="pir-section pir-section-break">
            <OIRSectionOpportunityLandscape rankedOpportunities={rankedOpportunities} />
            <NarrativeBlock>
              Maps the {includedCount} opportunities above the inclusion floor by category and strategic fit —
              revealing where value concentrations are strongest and which domains may be underexplored relative to their potential impact.
            </NarrativeBlock>
          </div>

          {/* ── 4. Financial Impact + QI-2 interpretation ────────────── */}
          <div className="pir-section pir-section-break">
            <OIRSectionFinancialImpact
              top10={top10}
              selectedOpportunities={selectedOpportunities}
              portfolioRangeLabel={portfolioRangeLabel}
              shortlistedRangeLabel={shortlistedRangeLabel}
            />
            <NarrativeBlock>
              Range format reflects scenario uncertainty — low end uses conservative assumptions, high end assumes full adoption.
              All projections are traceable to underlying assumptions in the DVA platform.
            </NarrativeBlock>
          </div>

          {/* ── 5. Execution Roadmap + QI-2 interpretation ───────────── */}
          <div className="pir-section pir-section-break">
            <OIRSectionExecutionRoadmap
              rankedOpportunities={rankedOpportunities}
              selectedOpportunities={selectedOpportunities}
            />
            <NarrativeBlock>
              Quick-win initiatives are front-loaded to generate early momentum and demonstrate ROI.
              More complex investments are staged for capability building and organizational readiness.
              Launch, Pilot, Monitor, and Defer categories provide full visibility into sequencing decisions across all assessed initiatives.
            </NarrativeBlock>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/* QI-2 NEW: Key Strategic Findings                           */}
          {/* ════════════════════════════════════════════════════════════ */}
          <div className="pir-section pir-section-break">
            <PrintSectionHeader
              title="Key Strategic Findings"
              subtitle="Interpretation summary for executive review"
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                {
                  num: '01',
                  title: 'Portfolio Concentration',
                  text: `The top ${finalistCount} initiatives represent the strongest risk-adjusted value in ${clientName}'s portfolio — maximizing ROI probability while managing execution complexity.`,
                },
                {
                  num: '02',
                  title: 'Strategic Alignment',
                  text: 'Recommended initiatives align across clinical outcomes, operational efficiency, and revenue optimization — providing a defensible basis for capital allocation decisions.',
                },
                {
                  num: '03',
                  title: 'Implementation Feasibility',
                  text: 'The portfolio balances effort levels across functional areas. Quick wins provide early proof points; strategic investments build long-term capability.',
                },
                {
                  num: '04',
                  title: 'Financial Credibility',
                  text: 'ROI projections use conservative baseline assumptions with explicit confidence ratings. Ranges reflect scenario analysis — not single-point estimates — supporting CFO-grade scrutiny.',
                },
                {
                  num: '05',
                  title: 'Governance Readiness',
                  text: 'This report provides the structured evidence base for governance review — standardized scoring, transparent assumptions, and executive-ready visualizations.',
                },
              ].map((insight) => (
                <div
                  key={insight.num}
                  className="pir-atomic"
                  style={{
                    display: 'flex',
                    gap: '16px',
                    padding: '16px 18px',
                    backgroundColor: '#F8F9FB',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <span
                    style={{
                      fontSize: '18px',
                      fontWeight: 800,
                      color: '#FF6E4C',
                      fontFamily: 'Montserrat, sans-serif',
                      flexShrink: 0,
                      width: '32px',
                    }}
                  >
                    {insight.num}
                  </span>
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: 700, color: '#18365E', margin: '0 0 4px 0' }}>
                      {insight.title}
                    </p>
                    <p style={{ fontSize: '12px', lineHeight: 1.65, color: '#374151', margin: 0 }}>
                      {insight.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/* QI-2 NEW: Methodology Appendix                             */}
          {/* ════════════════════════════════════════════════════════════ */}
          <div className="pir-section pir-section-break">
            <PrintSectionHeader
              title="Methodology"
              subtitle="DVA evaluation framework and scoring approach"
            />

            <div style={{ fontSize: '13px', lineHeight: 1.75, color: '#374151' }}>
              <p style={{ marginBottom: '16px' }}>
                The DVA (Data Value Accelerator) employs a structured, multi-dimensional
                evaluation framework to assess and prioritize data and analytics initiatives.
                Each opportunity is scored across <strong>38 prioritization measures</strong> organized
                into four evaluation dimensions:
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                {[
                  {
                    title: 'Strategic Alignment',
                    desc: "Measures how closely an initiative maps to the organization's stated strategic priorities, modernization goals, and governance mandates.",
                  },
                  {
                    title: 'Financial Impact',
                    desc: 'Quantifies projected ROI, payback period, annual benefit, and net present value using organization-specific assumptions and industry benchmarks.',
                  },
                  {
                    title: 'Implementation Complexity',
                    desc: 'Evaluates technical feasibility, resource requirements, integration dependencies, change management needs, and organizational readiness.',
                  },
                  {
                    title: 'Data Availability',
                    desc: 'Assesses the current state of data infrastructure, quality, accessibility, and interoperability required to execute the initiative.',
                  },
                ].map((dim) => (
                  <div
                    key={dim.title}
                    className="pir-atomic"
                    style={{
                      padding: '16px',
                      backgroundColor: '#F0F4F8',
                      borderRadius: '8px',
                      borderLeft: '3px solid #18365E',
                    }}
                  >
                    <p style={{ fontSize: '13px', fontWeight: 700, color: '#18365E', margin: '0 0 6px 0' }}>
                      {dim.title}
                    </p>
                    <p style={{ fontSize: '12px', lineHeight: 1.6, color: '#374151', margin: 0 }}>
                      {dim.desc}
                    </p>
                  </div>
                ))}
              </div>

              <p style={{ marginBottom: '16px' }}>
                Scores are normalized on a 1–100 scale using normalized scoring methodology
                to ensure fair comparison across initiatives of different types and scales.
                The composite ranking combines Business Value (BV) and Implementation Ease (IE)
                axes with configurable weighting — allowing organizations to emphasize quick
                wins, strategic investments, or balanced portfolios depending on their
                governance priorities.
              </p>

              <p style={{ marginBottom: '16px' }}>
                Financial projections use a three-scenario model (Conservative, Base, Aggressive)
                with explicit confidence ratings. All assumptions are traceable to source inputs —
                including organizational surveys, uploaded documentation, and industry benchmark data.
              </p>

              <div
                className="pir-atomic"
                style={{
                  padding: '16px 20px',
                  backgroundColor: '#18365E',
                  borderRadius: '8px',
                  color: '#ffffff',
                  marginTop: '8px',
                }}
              >
                <p style={{ fontSize: '12px', fontWeight: 600, color: '#FF6E4C', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Transparency Commitment
                </p>
                <p style={{ fontSize: '12px', lineHeight: 1.7, color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                  DVA is designed for auditability. Every score, ranking, and financial projection
                  can be traced to its underlying measures, weights, and assumptions within the
                  platform. This report represents a point-in-time snapshot — scores and projections
                  update dynamically as organizational context evolves.
                </p>
              </div>
            </div>
          </div>

          {/* ── Report Footer ────────────────────────────────────────── */}
          <div className="mt-12 pt-6 border-t border-[#e5e7eb] flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#717182]">
                Portfolio Strategy Report
              </p>
              <p className="text-[10px] text-[#FF6E4C] font-semibold">
                DVA · Adaptive Product
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-[#717182]">
                {clientName} · {formatDate()}
              </p>
              <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[#FF6E4C]">
                Early Adopter Preview
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}